import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import { ProductionProjectNoteStatusEnum } from "@/utils/enums";
import { api } from "@/utils/api";
import { ProductionNoteResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { RootState } from "@/utils/store";

type FormDataT = {
  content: string;
  status: ProductionProjectNoteStatusEnum;
};

interface Props {
  onClose: () => void;
  note?: ProductionNoteResource;
}

const NotesModal: FC<Props> = ({ onClose, note }) => {
  const alerts = useAlerts();

  const currentUserId = useSelector(
    (rootState: RootState) => rootState.auth.user!.id
  );

  const queryClient = useQueryClient();

  const { projectId } = useParams();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: { content: note?.content ?? "", status: note?.status },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ ...data }) => {
      try {
        const body = {
          content: data.content,
          status: data.status,
          userId: currentUserId,
        };

        if (note) {
          await api.put<never, { id: string }>(
            `/v1/production/projects/${projectId}/notes/${note.id}`,
            body
          );
        } else {
          await api.post<never, ProductionNoteResource>(
            `/v1/production/projects/${projectId}/notes`,
            body
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Note has been successfully ${!!note ? "updated" : "created"}`
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading={`${note ? "Edit" : "Add"} Note`} onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT, ProductionProjectNoteStatusEnum>
                name="status"
                control={control}
                label="Status"
                type="select"
                options={[
                  {
                    label: "Info",
                    value: ProductionProjectNoteStatusEnum.INFO,
                  },
                  {
                    label: "Warning",
                    value: ProductionProjectNoteStatusEnum.WARNING,
                  },
                  {
                    label: "Canceled",
                    value: ProductionProjectNoteStatusEnum.CANCELED,
                  },
                ]}
              />
              <FormField<FormDataT>
                name="content"
                control={control}
                label="Note"
                type="textarea"
                placeholder="Add note"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default NotesModal;
